import { Fragment, useEffect, useState } from "react";
import styled from "styled-components";
import { Button } from "../Items/Button";
import { Input } from "../Items/Input";
import { Select } from "../Items/Select";
import { useMQTTPublish } from "../../hooks/mqtt";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1.5em;
  width: 95%;
  max-width: 600px;
  text-align: start;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 1em;
  width: 100%;
`;

// The device this page is about stays centred above the settings it applies to
const DeviceRow = styled(Row)`
  justify-content: center;
`;

const Fields = styled.div`
  display: grid;
  grid-template-columns: 1fr 10em;
  align-items: center;
  gap: 0.75em 1em;
  width: 100%;
`;

const Label = styled.label`
  font-weight: bold;
`;

const Unit = styled.span`
  color: gray;
  font-weight: normal;
`;

// Select is box-sizing: border-box (Items/Select.js), but Input stays the
// browser default, content-box (Items/Input.js) -- so "width: 100%" of the
// same Fields grid column renders each one a different total size: Input's
// padding and border land on top of the column width, Select's are absorbed
// into it. Reconstructing Input's total width here -- the column plus its
// 2.4em padding and 4px border -- is what actually lines a dropdown up with
// the inputs beside it, rather than the coincidence a bare 100% would be.
const FieldSelect = styled(Select)`
  width: calc(100% + 2.4em + 4px);
  max-width: none;
`;

// The retain switch beside the Apply button
const Check = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-weight: bold;
  cursor: pointer;

  input {
    accent-color: rgb(0 79 255);
    width: 1.1em;
    height: 1.1em;
    cursor: pointer;
  }
`;

const Note = styled.p`
  color: gray;
  margin: 0;
`;

const StatusDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) => (props.status === true ? "green" : "gray")};
`;

export const DEVICE_STATES = ["STANDBY", "USER_TEST", "SILENT", "ACTIVE"];

// The camera-module/AIS load levels the settings document carries alongside
// `state`, one field per load (wecoenergy/denshi#387): a standing "ON" | "OFF" |
// "AUTO", where AUTO hands the load back to the state's own rule. `field` is
// where the config message reports the level in force; `statusField` is the
// resolved on/off in the status message. Both are absent on older firmware.
export const LOADS = [
  {
    key: "camera_module",
    label: "Camera module",
    field: "config_loads_camera_module",
    statusField: "camera_module_on",
  },
  { key: "ais", label: "AIS", field: "config_loads_ais", statusField: "ais_on" },
];

// Indexed by the value the config message carries (config::LoadLevel).
export const LOAD_LEVELS = ["AUTO", "ON", "OFF"];

// The PTO gains the settings topic accepts, with the range the firmware holds each
// one to (motor::kGainLimits). A value outside its range makes the device refuse the
// whole message, so we check them here rather than send one we know it will drop.
// `name` is what a settings message spells; `field` is what the config message on
// weco/<hostname>/config reports the set in force under, which is denshi's own
// catalog name for it (kConfigFields in src/app/log_schema.cpp).
export const PTO_GAINS = [
  {
    name: "damping_tension",
    field: "config_gains_damping_tension",
    label: "Damping (tension)",
    unit: "N·s/m",
    lo: 0.001,
    hi: 22640,
  },
  {
    name: "damping_return",
    field: "config_gains_damping_return",
    label: "Damping (return)",
    unit: "N·s/m",
    lo: 0.001,
    hi: 9902,
  },
  {
    name: "wall_onset",
    field: "config_gains_wall_onset",
    label: "Wall onset",
    unit: "m",
    lo: 0,
    hi: 0.166,
  },
  {
    name: "wall_stiffness",
    field: "config_gains_wall_stiffness",
    label: "Wall stiffness",
    unit: "N/m",
    lo: 0.001,
    hi: 50000,
  },
  {
    name: "wall_damping",
    field: "config_gains_wall_damping",
    label: "Wall damping",
    unit: "N·s/m",
    lo: 0.001,
    hi: 10000,
  },
  {
    name: "blend_half_band",
    field: "config_gains_blend_band",
    label: "Blend half-band",
    unit: "m/s",
    lo: 0.0001,
    hi: 0.2,
  },
];

// The rest of the configuration the device reports, in the order its record
// assembles it. None of it can be set from here — a settings message carries the
// gains and the commanded state and nothing else — so these are shown rather than
// offered, and the page reads them for what they are: what the machine is running.
//
// `flag` marks a value that is a boolean on the card. The firmware record is one
// float[], so every value on it arrives as a number whatever its source type, and a
// flag reads as 0 or 1 until it is rendered as one.
export const CONFIG_SECTIONS = [
  {
    title: "Geometry",
    fields: [
      {
        field: "config_geometry_contact_to_neutral",
        label: "Contact to neutral",
        unit: "rev",
      },
      {
        field: "config_geometry_motor_to_rack_sign",
        label: "Motor to rack sign",
      },
    ],
  },
  {
    title: "Access point",
    fields: [
      { field: "config_access_point_window", label: "Window", unit: "s" },
      { field: "config_access_point_period", label: "Period", unit: "s" },
    ],
  },
  {
    title: "Overspeed",
    fields: [
      {
        field: "config_overspeed_disarm_above",
        label: "Disarm above",
        unit: "rev/s",
      },
      {
        field: "config_overspeed_rearm_below",
        label: "Rearm below",
        unit: "rev/s",
      },
    ],
  },
  {
    title: "Load shed",
    fields: [
      { field: "config_load_shed_off_below", label: "Off below", unit: "%" },
      { field: "config_load_shed_on_above", label: "On above", unit: "%" },
    ],
  },
  {
    title: "Load shed voltage",
    fields: [
      {
        field: "config_load_shed_voltage_off_below",
        label: "Off below",
        unit: "V",
      },
      {
        field: "config_load_shed_voltage_on_above",
        label: "On above",
        unit: "V",
      },
    ],
  },
  {
    title: "ODrive",
    fields: [
      {
        field: "config_odrive_watchdog_timeout",
        label: "Watchdog timeout",
        unit: "s",
      },
      {
        field: "config_odrive_enable_watchdog",
        label: "Watchdog enabled",
        flag: true,
      },
      {
        field: "config_odrive_vel_integrator_gain",
        label: "Velocity integrator gain",
      },
      {
        field: "config_odrive_field_weakening",
        label: "Field weakening",
        flag: true,
      },
      {
        field: "config_odrive_modulation_setpoint",
        label: "Modulation setpoint",
      },
      {
        field: "config_odrive_current_soft_max",
        label: "Current soft max",
        unit: "A",
      },
      {
        field: "config_odrive_torque_constant",
        label: "Torque constant",
        unit: "N·m/A",
      },
      {
        field: "config_odrive_vel_limit",
        label: "Velocity limit",
        unit: "turns/s",
      },
      {
        field: "config_odrive_torque_mode_vel_limit",
        label: "Velocity limit in torque mode",
        flag: true,
      },
      {
        field: "config_odrive_heartbeat_rate",
        label: "Heartbeat rate",
        unit: "ms",
      },
      { field: "config_odrive_encoder_rate", label: "Encoder rate", unit: "ms" },
      { field: "config_odrive_iq_rate", label: "Iq rate", unit: "ms" },
      {
        field: "config_odrive_bus_voltage_rate",
        label: "Bus voltage rate",
        unit: "ms",
      },
      { field: "config_odrive_torques_rate", label: "Torques rate", unit: "ms" },
      { field: "config_odrive_error_rate", label: "Error rate", unit: "ms" },
      {
        field: "config_odrive_temperature_rate",
        label: "Temperature rate",
        unit: "ms",
      },
      {
        field: "config_odrive_overvoltage_trip",
        label: "Overvoltage trip",
        unit: "V",
      },
      {
        field: "config_odrive_undervoltage_trip",
        label: "Undervoltage trip",
        unit: "V",
      },
    ],
  },
  {
    title: "Brake resistor",
    fields: [
      { field: "config_brake_resistor_enabled", label: "Enabled", flag: true },
      { field: "config_brake_resistor_ohm", label: "Resistance", unit: "Ω" },
      { field: "config_brake_resistor_feedback", label: "Feedback", flag: true },
      {
        field: "config_brake_resistor_ramp_start",
        label: "Ramp start",
        unit: "V",
      },
      { field: "config_brake_resistor_ramp_end", label: "Ramp end", unit: "V" },
    ],
  },
];

// The one gain-group member a settings message cannot set, shown with the gains it
// arrives beside rather than in a section of its own.
const WALL_GUARDS = {
  field: "config_gains_wall_guards_return_stop",
  label: "Wall guards return stop",
  flag: true,
};

// Every field the config message carries, gains included: what the page reads off a
// publish and keeps.
const CONFIG_FIELDS = [
  ...PTO_GAINS.map((gain) => gain.field),
  ...LOADS.map((load) => load.field),
  WALL_GUARDS.field,
  ...CONFIG_SECTIONS.flatMap((section) =>
    section.fields.map((field) => field.field)
  ),
];

// The device holds a parameter as a 32-bit float and the config message widens it to
// a double, which spells out the part of the value the float never held: 300.2 comes
// back as 300.20001220703125. Rounding to the ~7 significant digits a float carries
// gives back the figure that was sent, without inventing precision the device does
// not have.
function fromFloat32(value) {
  return Number(value.toPrecision(7));
}

// A stable identity for "this device has reported nothing", so deriving a field's
// value from it does not make a new object every render.
const NOTHING_REPORTED = {};

// What a config message reports, by field. A message carries only the configuration
// groups that fit the device's outbox, so a field can be absent from one publish and
// back on the next: what is absent is left out here rather than read as zero, and the
// caller keeps the last value it saw. A gain of zero is a real value meaning no
// damping at all, so the two must not look alike.
function readConfig(config) {
  return CONFIG_FIELDS.reduce((values, field) => {
    const value = config?.[field];

    if (typeof value === "number") values[field] = fromFloat32(value);

    return values;
  }, {});
}

// A value the operator cannot change, as the box beside its label shows it: a flag
// as a word, everything else as the number the device reported.
function shownValue(field, value) {
  if (value === undefined) return "";

  return field.flag ? (value ? "Yes" : "No") : String(value);
}

export default function Settings({ wecs, mqttClient, messages }) {
  const [selectedHostname, setSelectedHostname] = useState(wecs?.[0]?.hostname);
  const [state, setState] = useState(DEVICE_STATES[0]);
  const [gains, setGains] = useState({});
  const [edited, setEdited] = useState([]);
  // The operator's pick per load key: "" (unchanged, left out of the message)
  // or one of LOAD_LEVELS. An untouched load is absent and shows the level
  // the device reports (see loadValue).
  const [loadLevels, setLoadLevels] = useState({});
  // What each device has been seen reporting, kept per hostname rather than for the
  // selected one alone: clearing on a WEC change would race the effect that fills
  // this, which only runs when a device's reported values change -- and a
  // configuration nobody is changing never changes.
  const [seen, setSeen] = useState({});
  const [retain, setRetain] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState("");
  const publish = useMQTTPublish(mqttClient);

  const wec = wecs?.find((wec) => wec.hostname === selectedHostname);
  const wecMessages = messages?.[wec?.hostname];
  const isOnline = wecMessages?.status?.status?.connected;
  const currentState = wecMessages?.status?.status?.state;
  const published = JSON.stringify(readConfig(wecMessages?.config?.config));
  const reported = seen[selectedHostname] || NOTHING_REPORTED;

  // Preselect the state the device reports so we don't offer a stale value
  useEffect(() => {
    if (currentState) setState(currentState);
  }, [currentState]);

  // Every value we have seen this device report, kept across publishes: a message
  // that leaves a group out is a group we have no news about, not one that changed.
  useEffect(() => {
    const values = JSON.parse(published);

    if (!selectedHostname || !Object.keys(values).length) return;

    setSeen((all) => {
      const reported = all[selectedHostname] || NOTHING_REPORTED;
      const next = { ...reported, ...values };

      return Object.keys(next).some((field) => next[field] !== reported[field])
        ? { ...all, [selectedHostname]: next }
        : all;
    });
  }, [published, selectedHostname]);

  // Drop what was typed when we switch to another WEC, so one device's tuning is
  // never offered as another's. What each device reports is kept.
  useEffect(() => {
    setGains({});
    setEdited([]);
    setLoadLevels({});
    setError("");
    setSent("");
  }, [selectedHostname]);

  if (!wecs?.length) return <div />;

  // What a gain's field shows: what the operator typed if they have typed in it, and
  // the set the device reports otherwise. Derived rather than copied into state, so a
  // device that reports the same tuning forever still fills the form.
  function gainValue(gain) {
    if (edited.includes(gain.name)) return gains[gain.name];

    return reported[gain.field] === undefined
      ? ""
      : String(reported[gain.field]);
  }

  // The operator's pick if any, else the level the config message reports;
  // "" if not reported. Derived rather than stored, like gainValue.
  function loadValue(load) {
    if (loadLevels[load.key] !== undefined) return loadLevels[load.key];

    return LOAD_LEVELS[reported[load.field]] || "";
  }

  function onGainChange(gain, value) {
    setGains((gains) => ({ ...gains, [gain.name]: value }));
    setEdited((edited) =>
      edited.includes(gain.name) ? edited : [...edited, gain.name]
    );
    setSent("");
  }

  // Every gain that carries a value, changed or not, so the retained message is the
  // whole tuning rather than the last edit to it: the device keeps the members a
  // message leaves out while it runs, but nothing on it persists a gain, so a reset
  // puts back the configured set for every member the retained message does not
  // name. An empty field is left out and keeps the value the device is running.
  function collectGains() {
    const payload = {};

    for (const gain of PTO_GAINS) {
      const text = gainValue(gain);

      if (text === undefined || text.trim() === "") continue;

      const value = Number(text);

      if (!Number.isFinite(value))
        return { error: `${gain.label} is not a number` };

      if (value < gain.lo || value > gain.hi)
        return {
          error: `${gain.label} must be between ${gain.lo} and ${gain.hi} ${gain.unit}`,
        };

      payload[gain.name] = value;
    }

    return { payload };
  }

  // One publish carrying state, the load levels and the gains together: the
  // topic holds one retained payload, so a message must say everything the
  // device should be. Every load showing a level is sent, changed or not, as
  // with the gains; one left "Unchanged" is left out, like an empty gain.
  function onApply() {
    const { payload: gainPayload, error: gainError } = collectGains();

    if (gainError) {
      setSent("");
      setError(gainError);
      return;
    }

    const topic = `weco/${wec.hostname}/settings`;
    const loadPayload = {};

    for (const load of LOADS) {
      if (loadValue(load)) loadPayload[load.key] = loadValue(load);
    }

    const payload = JSON.stringify({ state, ...loadPayload, ...gainPayload });

    setSending(true);
    setSent("");
    console.log(`Publishing ${payload} to ${topic}`);
    // Retained by default, so a device picks up its settings when it reconnects.
    // Unretained, the message reaches whoever is subscribed at the time and
    // leaves the retained one standing, which is the way to re-tune a device
    // that is on the link now without changing what the next one to reconnect
    // reads.
    publish(topic, payload, { retain }, (err) => {
      setSending(false);
      if (err) {
        console.error("Settings publish error:", err);
        setError("Failed to send settings");
        return;
      }
      setError("");
      setSent(`Sent ${payload} to ${topic}${retain ? " (retained)" : ""}`);
      setEdited([]);
      setLoadLevels({});
    });
  }

  function reportedField(field) {
    return (
      <Fragment key={field.field}>
        <Label as="div">
          {field.label} {field.unit && <Unit>({field.unit})</Unit>}
        </Label>
        <Input
          type="text"
          value={shownValue(field, reported[field.field])}
          placeholder="not reported"
          readOnly
          disabled
        />
      </Fragment>
    );
  }

  return (
    <Container>
      <DeviceRow>
        <StatusDot status={isOnline} />
        <Select
          value={selectedHostname || ""}
          onChange={(event) => setSelectedHostname(event.target.value)}
        >
          {wecs?.map((wec) => (
            <option key={wec.id || wec.hostname} value={wec.hostname}>
              {wec.name || wec.hostname || wec.id}
            </option>
          ))}
        </Select>
      </DeviceRow>
      <h3 style={{ margin: 0 }}>State</h3>
      <Fields>
        <Label htmlFor="state">
          Commanded{currentState ? <Unit> (running: {currentState})</Unit> : ""}
        </Label>
        <FieldSelect
          id="state"
          value={state}
          onChange={(event) => {
            setState(event.target.value);
            setSent("");
          }}
        >
          {DEVICE_STATES.map((deviceState) => (
            <option key={deviceState} value={deviceState}>
              {deviceState}
            </option>
          ))}
        </FieldSelect>
      </Fields>
      <h3 style={{ margin: 0 }}>Loads</h3>
      <Fields>
        {LOADS.map((load) => {
          const on = wecMessages?.status?.status?.[load.statusField];
          const running = on === undefined ? "" : on ? "on" : "off";

          return (
            <Fragment key={load.key}>
              <Label htmlFor={load.key}>
                {load.label}{running ? <Unit> (running: {running})</Unit> : ""}
              </Label>
              <FieldSelect
                id={load.key}
                value={loadValue(load)}
                onChange={(event) => {
                  setLoadLevels((all) => ({
                    ...all,
                    [load.key]: event.target.value,
                  }));
                  setSent("");
                }}
              >
                <option value="">Unchanged</option>
                {LOAD_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    {level === "AUTO" ? "Auto" : level === "ON" ? "On" : "Off"}
                  </option>
                ))}
              </FieldSelect>
            </Fragment>
          );
        })}
      </Fields>
      <Note>
        A standing level, shown as the device reports it and sent with Apply
        below alongside the state and the gains. On and Off hold the load
        there until replaced; Auto returns it to the state's own rule.
        "Unchanged" leaves the load out of the message.
      </Note>
      <h3 style={{ margin: 0 }}>PTO gains</h3>
      <Fields>
        {PTO_GAINS.map((gain) => (
          <Fragment key={gain.name}>
            <Label htmlFor={gain.name}>
              {gain.label} <Unit>({gain.unit})</Unit>
            </Label>
            <Input
              id={gain.name}
              type="number"
              inputMode="decimal"
              min={gain.lo}
              max={gain.hi}
              step="any"
              placeholder={
                reported[gain.field] !== undefined
                  ? String(reported[gain.field])
                  : "unchanged"
              }
              value={gainValue(gain)}
              onChange={(event) => onGainChange(gain, event.target.value)}
            />
          </Fragment>
        ))}
        {reportedField(WALL_GUARDS)}
      </Fields>
      <Note>
        These are the gains the device is running, which a settings message changes
        without writing to the card: they go back to the card's set on a reset. A
        gain left empty keeps the value the device is running.
      </Note>
      <Row>
        <Button onClick={onApply} disabled={sending || !wec}>
          Apply
        </Button>
        <Check>
          <input
            type="checkbox"
            checked={retain}
            onChange={(event) => {
              setRetain(event.target.checked);
              setSent("");
            }}
          />
          Retain
        </Check>
      </Row>
      {sent && <p style={{ color: "gray" }}>{sent}</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}
      {CONFIG_SECTIONS.map((section) => (
        <Fragment key={section.title}>
          <h3 style={{ margin: 0 }}>{section.title}</h3>
          <Fields>{section.fields.map(reportedField)}</Fields>
        </Fragment>
      ))}
      <Note>
        Everything below the gains is reported by the device and cannot be set from
        here: a settings message carries the state and the gains, nothing else.
      </Note>
    </Container>
  );
}
